// Typage minimal pour les erreurs API et la fonction de traduction
type U2000ApiError = {
  status?: any;
  code?: string;
  message?: string;
  data?: {
    errors?: Array<{
      errorCode?: string;
      code?: string;
      description?: string;
      parameters?: Array<{ name: string }>;
    }>;
  };
};

export type U2000ApiErrorResult = {
  genericErrors: Array<{ title: string; text: string }>;
  fieldErrors: Record<string, string>;
  otherErrors: Record<string, string | string[]>;
};

export type TranslateFn = (key: string, options?: any) => string;
export type LuHandleAppiErrorFn = (t: TranslateFn, nameSpace: string, err: any, errorResult: U2000ApiErrorResult) => void;

export const U2000HandleApiError = (error: U2000ApiError, t: TranslateFn, nameSpace: string, luHandleAppiErrorFn?: LuHandleAppiErrorFn): U2000ApiErrorResult => {
  let errorResult: U2000ApiErrorResult = { genericErrors: [], fieldErrors: {}, otherErrors: {} };

  // Si l'objet d'erreur est imbriqué sous une clé 'error', l'utiliser automatiquement
  let realError: any = error;
  if (realError && typeof realError === "object" && "error" in realError && realError.error) {
    realError = realError.error;
  }

  // Supporte Axios (error.response.status) et fallback sur error.status
  const status = (realError && realError.response?.status) ?? realError?.status;

  if (!realError || !status) {
    errorResult.genericErrors.push({
      title: t("errorTitle"),
      text: "handleApiError: Invalid error object",
    });
    return errorResult;
  }

  switch (status) {
    case 404:
      errorResult = handleNotFoundError(realError, t);
      break;
    case 400:
      errorResult = handleBadRequestError(realError, t, nameSpace, luHandleAppiErrorFn);
      break;
    case 401:
      errorResult = handleUnauthorizedError(realError, t);
      break;
    default:
      errorResult.genericErrors.push({
        title: t("errorTitle"),
        text: t("errorTRM-B9999"),
      });
      break;
  }
  return errorResult;
};

const handleNotFoundError = (error: U2000ApiError, t: TranslateFn): U2000ApiErrorResult => {
  const errorResult: U2000ApiErrorResult = { genericErrors: [], fieldErrors: {}, otherErrors: {} };
  if (error.data?.errors?.length && error.data.errors[0].code === "TRM-B0001") {
    errorResult.genericErrors.push({
      title: t("errorTitle"),
      text: t("errorTRM-B0001"),
    });
  } else {
    errorResult.genericErrors.push({
      title: t("errorTitle"),
      text: t("errorTRM-B9999"),
    });
  }
  return errorResult;
};

const handleUnauthorizedError = (error: U2000ApiError, t: TranslateFn): U2000ApiErrorResult => {
  const errorResult: U2000ApiErrorResult = { genericErrors: [], fieldErrors: {}, otherErrors: {} };
  if (error.data?.errors?.length) {
    if (error.data.errors[0].errorCode === "A0002") {
      errorResult.genericErrors.push({
        title: t("errorTitle"),
        text: t("errorA0001"),
      });
    }
  }
  return errorResult;
};

const handleBadRequestError = (error: U2000ApiError, t: TranslateFn, nameSpace: string, luHandleAppiErrorFn?: LuHandleAppiErrorFn): U2000ApiErrorResult => {
  const errorResult: U2000ApiErrorResult = { genericErrors: [], fieldErrors: {}, otherErrors: {} };

  if (error.data?.errors?.length) {
    error.data.errors.forEach((err) => {
      switch (err.errorCode) {
        case "B0007":
          if (err.parameters?.length) {
            err.parameters.forEach((param) => {
              const paramName = param["name"];
              const fieldValue = t(paramName, { ns: nameSpace });
              errorResult.fieldErrors[paramName] = t("errorB0007", { fieldName: fieldValue });
            });
          } else if (err.description?.trim()) {
            // Ex. doublon U3129 initForCreate : B0007 + description métier, sans paramètres de champ.
            errorResult.genericErrors.push({
              title: t("errorTitle", { ns: nameSpace }),
              text: err.description.trim(),
            });
          }
          break;
        case "B0002":
        case "B0003":
        case "B0004":
        case "B0005":
        case "B0006":
        case "B0008":
        case "B0009":
          errorResult.genericErrors.push({
            title: t("errorTitle", { ns: nameSpace }),
            text: t(`error${err.errorCode}`, { ns: nameSpace }),
          });
          break;
        case "B0010":
          if (err.parameters?.length) {
            err.parameters.forEach((param) => {
              const paramName = param["name"];
              const fieldValue = t(paramName, { ns: nameSpace });
              errorResult.fieldErrors[paramName] = t("errorB0010", { fieldName: fieldValue });
            });
          }
          break;
        default:
          if (luHandleAppiErrorFn) {
            luHandleAppiErrorFn(t, nameSpace, err, errorResult);
          }
          if (Object.keys(errorResult.fieldErrors).length === 0 && errorResult.genericErrors.length === 0 && Object.keys(errorResult.otherErrors).length === 0) {
            errorResult.genericErrors.push({
              title: t("errorTitle", { ns: nameSpace }),
              text: t("errorTRM-B997", {
                errorCode: err.errorCode,
                errorMessage: err.description,
              }),
            });
          }
      }
    });
  }
  return errorResult;
};
